/**
 * "What needs you" under the Finish Line's Next (job page v2, slice 5): the
 * classic page listed every blocker with its own button; the new page showed
 * only Next. Corbin's call (2026-10-08): Next, then up to 3 more, then the
 * Help Desk check-up strip. The rest stays one click away in "See all".
 *
 * Each row is a quiet line (the colored dot says whose move it is) with the
 * promise's own action as a quiet link, so Next stays the only filled button.
 */
import { cn } from "@/lib/utils";
import type { FinishLine, JobPromise, PromiseAction } from "@/lib/finish-line";
import { stateLabel } from "./finishLineModel";

const OPEN_STATES: JobPromise["state"][] = ["blocked", "missed", "open", "waiting"];

/**
 * Up to `max` open promises besides Next: ours first (blocked, missed, then
 * open), then what we're waiting on. Engine order inside each.
 */
export function alsoOpen(fl: FinishLine, nextId: string | null, max = 3): JobPromise[] {
    const rank = (p: JobPromise) => (p.state === "waiting" ? 1 : 0) * 10 + OPEN_STATES.indexOf(p.state);
    return fl.promises
        .filter((p) => p.id !== nextId && OPEN_STATES.includes(p.state))
        .map((p, i) => ({ p, i }))
        .sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i)
        .slice(0, max)
        .map((x) => x.p);
}

/** Open promises besides Next, in all. */
export function alsoOpenCount(fl: FinishLine, nextId: string | null): number {
    return fl.promises.filter((p) => p.id !== nextId && OPEN_STATES.includes(p.state)).length;
}

const DOT: Record<string, string> = {
    blocked: "bg-red-600",
    missed: "bg-amber-600",
    open: "bg-amber-600",
    waiting: "bg-blue-600",
};

export function AlsoOpenList({
    fl,
    nextId,
    now,
    busy,
    onRun,
    runnable,
}: {
    fl: FinishLine;
    nextId: string | null;
    now: Date;
    busy: boolean;
    onRun: (a: PromiseAction) => void;
    runnable: (a: PromiseAction | undefined) => a is PromiseAction;
}) {
    const rows = alsoOpen(fl, nextId);
    if (rows.length === 0) return null;
    const more = alsoOpenCount(fl, nextId) - rows.length;
    return (
        <div className="grid gap-0.5 border-t pt-3" data-testid="finish-line-also">
            <span className="text-[13px] font-semibold text-muted-foreground">Also open</span>
            <ul className="m-0 grid list-none gap-0 p-0">
                {rows.map((p) => (
                    <li
                        key={p.id}
                        className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 py-1 text-[14.5px]"
                        data-testid="finish-line-also-row"
                    >
                        <span
                            className={cn("inline-block h-2 w-2 shrink-0 rounded-full", DOT[p.state] ?? "bg-muted")}
                            aria-hidden
                        />
                        <span className="min-w-0 flex-1 text-foreground">{p.title}</span>
                        <span className="text-[13px] text-muted-foreground">{stateLabel(p, now)}</span>
                        {runnable(p.action) && (
                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => onRun(p.action!)}
                                className="rounded-sm bg-transparent p-0 text-[14px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
                            >
                                {p.action.label} →
                            </button>
                        )}
                    </li>
                ))}
            </ul>
            {more > 0 && <span className="text-[13px] text-muted-foreground">{more} more below in See all.</span>}
        </div>
    );
}
